import React, { useEffect, useRef } from "react";
import Chart from "chart.js";

import useTranslation from "src/translations/useTranslation";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";
import { TRAVEL_CYCLE_TARGET_RATE } from "@shared/constants/travel-cycle-causes.enum";
import { TravelCycleSummary } from "@shared/interfaces/travel-cycle-analysis.interface";

import { lineLabelPlugin } from "../../chartPlugins";
import { dailyRates, dayLabel, formatCount, formatPercent, movingAverage, percentOf } from "../../cycleUtils";
import { ChartContainer } from "../../styles";

/** Colors of the daily chart lines. */
export const DAILY_COLORS = { rate: "#2a78d6", trend: "#9e9e9e", average: "#1f9d63", target: "#e74c3c" };

/** Props for the CycleDaily organism. */
interface CycleDailyProps {
  summary: TravelCycleSummary | null;
}

/**
 * Complete cycle per day of the filter, with the 5-day moving average (trend), the average of the period,
 * labeled on the line, and the target.
 */
const CycleDaily: React.FC<CycleDailyProps> = ({ summary }) => {

  const { t, i18n } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);
  const average = summary ? percentOf(summary.complete, summary.total) : 0;

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (!canvasRef.current || !summary || !summary.days.length) return;

    const rates = dailyRates(summary.daily);
    const values = rates.filter((v): v is number => v !== null).concat(average);
    const minRate = Math.max(0, Math.floor((Math.min(...values) - 5) / 5) * 5);

    chartRef.current = new Chart(canvasRef.current, {
      type: "line",
      data: {
        labels: summary.days.map((day) => dayLabel(day, i18n.language)),
        datasets: [
          {
            label: t(TravelAnalysisMessages.cycleKpiComplete) ?? "",
            data: rates,
            borderColor: DAILY_COLORS.rate,
            backgroundColor: DAILY_COLORS.rate,
            borderWidth: 2,
            pointRadius: 3,
            fill: false,
            lineTension: 0,
            spanGaps: true
          },
          {
            label: t(TravelAnalysisMessages.movingAverage) ?? "",
            data: movingAverage(rates),
            borderColor: DAILY_COLORS.trend,
            borderWidth: 1.5,
            borderDash: [5, 4],
            pointRadius: 0,
            fill: false,
            lineTension: 0.3,
            spanGaps: true
          },
          {
            label: t(TravelAnalysisMessages.periodAverage) ?? "",
            data: summary.days.map(() => Math.round(average * 10) / 10),
            borderColor: DAILY_COLORS.average,
            borderWidth: 2.5,
            borderDash: [2, 3],
            pointRadius: 0,
            fill: false
          },
          {
            label: `${t(TravelAnalysisMessages.target)} (${TRAVEL_CYCLE_TARGET_RATE}%)`,
            data: summary.days.map(() => TRAVEL_CYCLE_TARGET_RATE),
            borderColor: DAILY_COLORS.target,
            borderWidth: 1,
            borderDash: [6, 4],
            pointRadius: 0,
            fill: false
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 },
        legend: { display: false },
        tooltips: {
          mode: "index",
          intersect: false,
          filter: (item) => item.datasetIndex === 0 || item.datasetIndex === 1,
          callbacks: {
            label: (item, data) => ` ${data.datasets?.[item.datasetIndex ?? 0]?.label ?? ""}: ${formatPercent(Number(item.yLabel))}`,
            afterBody: (items) => {
              const day = summary.daily[items[0]?.index ?? 0];

              return day ? ["", ` ${t(TravelAnalysisMessages.tripsUnit)}: ${formatCount(day[0])}`] : [];
            }
          }
        },
        scales: {
          xAxes: [{ gridLines: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 16 } }],
          yAxes: [{ ticks: { min: minRate, max: 100, stepSize: 5, callback: (value) => `${value}%` } }]
        }
      },
      plugins: [lineLabelPlugin(`${t(TravelAnalysisMessages.averageShort)} ${formatPercent(average)}`, average, DAILY_COLORS.average)]
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, i18n.language]);

  useEffect(() => () => {
    if (chartRef.current) chartRef.current.destroy();
  }, []);

  return (
    <ChartContainer>
      <div className="chart-title">{t(TravelAnalysisMessages.dailyTitle)}</div>
      <p className="chart-hint">{t(TravelAnalysisMessages.dailyHint)}</p>
      {!summary || !summary.days.length ? (
        <div className="chart-empty">{t(TravelAnalysisMessages.noGroupedData)}</div>
      ) : (
        <>
          <div className="chart-legend">
            <span><i style={{ borderTopColor: DAILY_COLORS.rate }} />{t(TravelAnalysisMessages.cycleKpiComplete)}</span>
            <span><i className="dashed" style={{ borderTopColor: DAILY_COLORS.trend }} />{t(TravelAnalysisMessages.movingAverage)}</span>
            <span>
              <i className="dotted" style={{ borderTopColor: DAILY_COLORS.average }} />
              {`${t(TravelAnalysisMessages.periodAverage)} (${formatPercent(average)})`}
            </span>
            <span>
              <i className="dashed" style={{ borderTopColor: DAILY_COLORS.target }} />
              {`${t(TravelAnalysisMessages.target)} (${TRAVEL_CYCLE_TARGET_RATE}%)`}
            </span>
          </div>
          <div className="daily-chart"><canvas ref={canvasRef} /></div>
        </>
      )}
    </ChartContainer>
  );
};

export default CycleDaily;
